/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Button, Modal, Form, Upload, message, Alert, Progress } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import activitiesService from "../../services/activitiesService";
import resourceService from "../../services/recursosService";
import useAuth from "../../hooks/useAuth";
import PageHeader from "../headers/PageHeader";
import { useUser } from "../../context/UserContext";

const { Dragger } = Upload;

const UploadButton = ({
  eventId,
  tipo = "normal",
  extras = true,
  protocol = "",
  activity = "",
  onUploadComplete,
}) => {
  const [visible, setVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const user = useUser();

  const handleCancel = () => {
    setVisible(false);
    setFiles([]);
    setProgress([]);
    setOverallProgress(0);
    form.resetFields();
  };

  const beforeUpload = (file) => {
    const isImageOrPdf =
      file.type.startsWith("image/") ||
      file.type === "application/pdf" ||
      file.type === "application/xlsl" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isImageOrPdf) {
      message.error(
        "Solo se permiten archivos de tipo imagen (JPG, PNG), xlsx o PDF."
      );
      return Upload.LIST_IGNORE;
    }

    const isSizeValid = file.size / (1024 * 1024) < 50;
    if (!isSizeValid) {
      message.error("El tamaño del archivo no puede superar los 50 MB.");
      return Upload.LIST_IGNORE;
    }

    // Permitir múltiples archivos
    setFiles((prev) => {
      const newFiles = [...prev, file];
      // Eliminar duplicados por nombre
      return newFiles.filter(
        (f, i, arr) => arr.findIndex((x) => x.name === f.name) === i
      );
    });
    return false;
  };

  const handleSubmit = async () => {
    if (!files.length) {
      message.warning("Selecciona al menos un archivo antes de continuar.");
      return;
    }

    setLoading(true);
    setProgress(Array(files.length).fill(0));
    setOverallProgress(0);
    let completados = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fileName = file.name;
        const fileType = file.type.split("/")[0];
        const fileExtension = fileName.split(".").pop().toLowerCase();

        // Determinar la ruta del archivo
        let filePath = "";
        if (extras) {
          if (tipo !== "saldo") {
            filePath = `eventdocuments/${eventId}/extras/${fileName}`;
          } else {
            filePath = `eventdocuments/${eventId}/extras/soporteCosto.${fileExtension}`;
          }
        } else {
          filePath = `eventdocuments/${eventId}/${protocol}/${activity}`;
        }

        // Crear el recurso usando crearRecurso_v2
        const data = JSON.stringify({
          name: fileName,
          path: filePath,
          description: fileName,
          type: fileType,
          extension: fileExtension,
        });

        console.log("📤 Datos enviados a crearRecurso_v2:", {
          fileName,
          filePath,
          fileType,
          fileExtension,
          data,
        });

        const token = user?.idToken || "";

        const response = await resourceService.crearRecurso_v2(token, data);

        const uploadUrl = response?.result?.url || null;
        if (!uploadUrl) {
          console.error(
            "❌ No se recibió URL de carga en la respuesta:",
            response
          );
          throw new Error("No se recibió una URL de carga");
        }

        // Subir archivo a S3 con progreso usando la URL proporcionada
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", uploadUrl);
          xhr.setRequestHeader("Content-Type", file.type);

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percent = Math.round((event.loaded / event.total) * 100);
              setProgress((prev) => {
                const copy = [...prev];
                copy[i] = percent;
                return copy;
              });
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              // Registrar en el servicio de actividades

              resolve();
            } else {
              reject(
                new Error(`Fallo la carga del archivo: ${xhr.statusText}`)
              );
            }
          };

          xhr.onerror = () =>
            reject(new Error("Error de red al cargar archivo"));

          xhr.send(file);
        });

        completados++;
        setOverallProgress(Math.round((completados / files.length) * 100));
        message.success(`${file.name} subido con éxito.`);
      } catch (error) {
        console.error("❌ Error al cargar archivo:", error);
        message.error(`Error al cargar el archivo ${file.name}`);
      }
    }

    // message.success("Carga de archivos completada");
    handleCancel();
    setLoading(false);

    // Notificar que se completó la carga
    if (onUploadComplete) {
      onUploadComplete();
    }
  };

  const asignarUrls = async (registro) => {
    try {
      await activitiesService.funcionCargarS3(registro);
    } catch (error) {
      console.error(error);
      message.error("No se logró hacer la consulta");
    }
  };

  return (
    <>
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Form.Item label="Archivo">
          <Dragger
            customRequest={() => {}}
            showUploadList={
              files.length ? files.map((f) => ({ name: f.name })) : false
            }
            beforeUpload={beforeUpload}
            multiple={true}
            accept="image/*,.pdf,.xlsx,.xls"
            onRemove={(file) =>
              setFiles((prev) => prev.filter((f) => f.name !== file.name))
            }
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Haz clic o arrastra los archivos aquí
            </p>
            <p className="ant-upload-hint">
              Solo se permiten archivos de tipo imagen (JPG, PNG), xlsx o PDF.
              Tamaño máximo: 50 MB.
            </p>
          </Dragger>
        </Form.Item>

        {/* Barra de progreso general */}
        {loading && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Progreso general: {overallProgress}%</strong>
            </div>
            <Progress percent={overallProgress} status="active" />

            {/* Progreso individual */}
            <div style={{ marginTop: 16 }}>
              <strong>Progreso por archivo:</strong>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {files.map((file, idx) => (
                  <li key={file.name} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "12px" }}>{file.name}</span>
                      <span style={{ fontSize: "12px" }}>
                        {progress[idx] || 0}%
                      </span>
                    </div>
                    <Progress
                      percent={progress[idx] || 0}
                      size="small"
                      showInfo={false}
                      style={{ marginTop: 4 }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            Cargar archivos
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default UploadButton;
